import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useAuthStore } from './lib/store/authStore';
import { listenForAuthChanges } from './lib/authSync';
import { requestNotificationPermission, subscribeToPush } from './lib/notifications';
import { authApi } from './lib/api';
import { UserRole } from './lib/types';
import './i18n/config'; // Initialize i18n

// Pages - Lazy loaded for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ClaimOrderPage = lazy(() => import('./pages/ClaimOrderPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const CreateProductPage = lazy(() => import('./pages/CreateProductPage'));
const CreateProductWizardPage = lazy(() => import('./pages/CreateProductWizardPage'));
const ProductGenerationsPage = lazy(() => import('./pages/ProductGenerationsPage'));
const ProductGenerationReviewPage = lazy(() => import('./pages/ProductGenerationReviewPage'));
const EditProductPage = lazy(() => import('./pages/EditProductPage'));
const GiftBoxesPage = lazy(() => import('./pages/GiftBoxesPage'));
const ConfigureGiftBoxPage = lazy(() => import('./pages/ConfigureGiftBoxPage'));
const ClientGiftBoxesPage = lazy(() => import('./pages/ClientGiftBoxesPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const SendNotificationPage = lazy(() => import('./pages/SendNotificationPage'));
const GuestAccessPage = lazy(() => import('./pages/GuestAccessPage'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const CouponsPage = lazy(() => import('./pages/CouponsPage'));
const PromptsPage = lazy(() => import('./pages/PromptsPage'));

// Layout
import DashboardLayout from './components/layouts/DashboardLayout';
import { ToastContainer } from './components/Toast';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  // Double check: isAuthenticated should match user presence
  // This prevents issues with stale persisted state
  const actuallyAuthenticated = !!user && isAuthenticated;

  return actuallyAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  
  if (user?.role !== UserRole.ADMIN) {
    return <Navigate to="/giftboxes" replace />;
  }
  
  return <>{children}</>;
}

function GiftBoxesRoute() {
  const { user } = useAuthStore();
  
  if (user?.role === UserRole.CLIENT) {
    return <ClientGiftBoxesPage />;
  }
  
  return <GiftBoxesPage />;
}

function DashboardRoute() {
  const { user } = useAuthStore();
  
  if (user?.role === UserRole.CLIENT) {
    return <Navigate to="/giftboxes" replace />;
  }
  
  return <DashboardPage />;
}

function App() {
  const { checkAuth, logout, user, isAuthenticated } = useAuthStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const waitForUserGesture = (): Promise<void> => {
    return new Promise((resolve) => {
      const handler = () => {
        window.removeEventListener('click', handler);
        resolve();
      };
      window.addEventListener('click', handler, { once: true });
    });
  };
  
  // Listen for auth changes from landing page
  useEffect(() => {
    let isProcessing = false; // Flag to prevent processing same message multiple times
    
    const cleanup = listenForAuthChanges((type) => {
      // Prevent processing if already handling a message
      if (isProcessing) {
        return;
      }

      // Don't process auth messages on public pages (guest-access, partner)
      if (location.pathname === '/guest-access' || location.pathname.startsWith('/partner/')) {
        return;
      }

      if (type === 'login') {
        // Landing page logged in, check auth here too
        isProcessing = true;
        checkAuth().finally(() => {
          isProcessing = false;
        });
      } else if (type === 'logout') {
        // Landing page logged out, logout here too
        // Skip broadcast to prevent loop (we're responding to external logout)
        isProcessing = true;
        logout(true).finally(() => {
          isProcessing = false;
        });
      }
    });

    // Also check auth when page becomes visible (user navigated back from landing).
    // Use silent: true so we don't set isLoading and unmount the whole tree (modals, page state).
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Don't check auth on public pages (guest-access, partner)
        if (location.pathname === '/guest-access' || location.pathname.startsWith('/partner/')) {
          return;
        }
        checkAuth({ silent: true });
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cleanup();
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAuth, logout, location.pathname]);

  useEffect(() => {
    // Don't check auth if we're on guest-access page (guests don't need auth)
    // Check both exact match and with trailing slash
    if (location.pathname === '/guest-access' || location.pathname === '/guest-access/') return;
    if (location.pathname.startsWith('/partner/')) return;
    const apiKey = searchParams.get('apiKey');
    if (apiKey && (apiKey.startsWith('guest_') || apiKey === 'guest')) return;
    checkAuth();
  }, [checkAuth, location.pathname, searchParams]);

  // Subscribe client users to push notifications when logged in
  useEffect(() => {
    // Don't setup push notifications on public pages (guest-access, partner)
    if (location.pathname === '/guest-access' || location.pathname.startsWith('/partner/')) {
      return;
    }

    const setupClientPushNotifications = async () => {
      // Only proceed if user is authenticated and is a CLIENT
      if (!isAuthenticated || !user || user.role !== UserRole.CLIENT) {
        return;
      }

      if (!('serviceWorker' in navigator) || !('Notification' in window)) return;

      // Check if already subscribed (avoid re-subscribing)
      if (user.pushSubscriptions && user.pushSubscriptions.length > 0) {
        // Check if current subscription exists
        try {
          const registration = await navigator.serviceWorker.ready;
          const existingSubscription = await registration.pushManager.getSubscription();
          if (existingSubscription) {
            // Check if this subscription is already in the user's list
            const isAlreadySubscribed = user.pushSubscriptions.some(
              (sub) => sub.endpoint === existingSubscription.endpoint
            );
            if (isAlreadySubscribed) return;
          }
        } catch (error) {
          console.error('Error checking existing subscription:', error);
        }
      }

      try {
        // Request notification permission (may require user gesture)
        if (Notification.permission === 'default') {
          await waitForUserGesture();
        }
        const permission = await requestNotificationPermission();
        
        if (permission !== 'granted') {
          return;
        }

        // Subscribe to push notifications
        const subscription = await subscribeToPush();
        
        if (subscription) {
          // Convert subscription to format expected by backend
          const subscriptionData = {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
            },
          };

          // Send subscription to backend
          try {
            await authApi.subscribePush(subscriptionData);
            // Refresh user data to get updated pushSubscriptions
            // Only refresh if not on public pages
            if (location.pathname !== '/guest-access' && !location.pathname.startsWith('/partner/')) {
              checkAuth();
            }
          } catch (error) {
            console.error('❌ Failed to register client push subscription:', error);
          }
        }
      } catch (error) {
        console.error('❌ Error setting up client push notifications:', error);
      }
    };

    // Setup push notifications for client users
    if (isAuthenticated && user && user.role === UserRole.CLIENT) {
      setupClientPushNotifications();
    }
  }, [isAuthenticated, user, checkAuth, location.pathname]);

  // Refresh access token every 10 min while the user is logged in (access token expires in 15 min)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const intervalMs = 10 * 60 * 1000; // 10 minutes
    const tid = setInterval(() => {
      authApi.refresh().catch(() => { /* ignore; next request will retry refresh or logout */ });
    }, intervalMs);
    return () => clearInterval(tid);
  }, [isAuthenticated, user]);

  const LoadingFallback = () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
    </div>
  );

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/partner/:token" element={<PartnerPage />} />
      <Route path="/guest-access" element={<GuestAccessPage />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <DashboardLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardRoute />} />
        <Route 
          path="products" 
          element={
            <AdminRoute>
              <ProductsPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="products/create" 
          element={
            <AdminRoute>
              <CreateProductPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="products/create-wizard" 
          element={
            <AdminRoute>
              <CreateProductWizardPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="products/generations" 
          element={
            <AdminRoute>
              <ProductGenerationsPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="products/generations/:id/review" 
          element={
            <AdminRoute>
              <ProductGenerationReviewPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="products/:id" 
          element={
            <AdminRoute>
              <EditProductPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="giftboxes" 
          element={<GiftBoxesRoute />} 
        />
        <Route 
          path="giftboxes/:id/configure" 
          element={
            <PrivateRoute>
              <ConfigureGiftBoxPage />
            </PrivateRoute>
          } 
        />
        <Route 
          path="orders" 
          element={
            <AdminRoute>
              <OrdersPage />
            </AdminRoute>
          } 
        />
        <Route path="orders/claim" element={<ClaimOrderPage />} />
        <Route 
          path="users" 
          element={
            <AdminRoute>
              <UsersPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="categories" 
          element={
            <AdminRoute>
              <CategoriesPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="prompts" 
          element={
            <AdminRoute>
              <PromptsPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="coupons" 
          element={
            <AdminRoute>
              <CouponsPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="api-keys" 
          element={
            <AdminRoute>
              <ApiKeysPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="notifications" 
          element={
            <AdminRoute>
              <SendNotificationPage />
            </AdminRoute>
          } 
        />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <ToastContainer />
    </>
  );
}

export default App;
